import { useState, useEffect } from 'react';
import { Typography, Modal, Tag, Space, Empty } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { colors } from '@festpix/ui';
import type { ScheduleSlot } from '@festpix/types';
import { getBoothScheduleSlots } from '@festpix/services';

const { Text } = Typography;

interface BoothScheduleBadgeProps {
  boothId: string;
  boothName: string;
}

function getCurrentTimeAsHHMM(): string {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

function getNextSlot(slots: ScheduleSlot[]): ScheduleSlot | null {
  const currentTime = getCurrentTimeAsHHMM();
  return slots.find((slot) => slot.endTime > currentTime) || null;
}

export default function BoothScheduleBadge({ boothId, boothName }: BoothScheduleBadgeProps) {
  const [slots, setSlots] = useState<ScheduleSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    loadSchedule();
  }, [boothId]);

  const loadSchedule = async () => {
    try {
      const slotsData = await getBoothScheduleSlots(boothId);
      setSlots(slotsData);
    } catch (error) {
      console.error('Error loading schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;

  const nextSlot = getNextSlot(slots);
  if (!nextSlot) return null;

  const currentTime = getCurrentTimeAsHHMM();
  const isActive = nextSlot.startTime <= currentTime && nextSlot.endTime > currentTime;
  const nextUserNames = nextSlot.assignedUsers.map((u) => u.userName).join(', ');

  return (
    <>
      <div
        onClick={() => setModalVisible(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: `${colors.info}15`,
          padding: '6px 16px',
          borderRadius: 20,
          marginTop: 8,
          cursor: 'pointer',
        }}
      >
        <ClockCircleOutlined style={{ color: colors.info, fontSize: 13 }} />
        <Text style={{ color: colors.info, fontSize: 13, fontWeight: 500 }}>
          {isActive ? `Agora: ${nextUserNames}` : `Proximo: ${nextUserNames} as ${nextSlot.startTime}`}
        </Text>
      </div>

      <Modal
        title={
          <span>
            <ClockCircleOutlined style={{ marginRight: 8, color: colors.info }} />
            Escala do Dia - {boothName}
          </span>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={500}
      >
        {slots.length === 0 ? (
          <Empty description="Nenhuma escala cadastrada para hoje" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 0' }}>
            {slots.map((slot) => {
              const isCurrent = slot.startTime <= currentTime && slot.endTime > currentTime;
              return (
                <div
                  key={slot.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 12px',
                    background: isCurrent ? `${colors.success}10` : undefined,
                    border: isCurrent ? `1px solid ${colors.success}40` : '1px solid #f0f0f0',
                    borderRadius: 8,
                  }}
                >
                  <Text strong style={{ minWidth: 100 }}>
                    {slot.startTime} - {slot.endTime}
                  </Text>
                  <Space wrap size={4}>
                    {slot.assignedUsers.map((u) => (
                      <Tag key={u.userId} color={isCurrent ? colors.success : colors.info}>
                        {u.userName}
                      </Tag>
                    ))}
                  </Space>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </>
  );
}
